import { Spacing } from '@persona-id/design-tokens';
import {
  AlignLeftIcon,
  AlignRightIcon,
  CenterAlignHorizontalIcon,
} from '@persona-id/design-tokens/icons';
import { useCallback } from 'react';
import { batch, useDispatch } from 'react-redux';
import { change } from 'redux-form';
import styled from 'styled-components';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import { ToggleStyleInput } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import {
  SectionColumn,
  SectionRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import Checkbox from 'design-system/components/Checkbox/Checkbox';
import { ConfigValueDirection, StyleValueJustify } from 'lib/inquiryStyles';
import {
  FooterComponent,
  FooterComponentConfig,
  isHorizontalConfigSetting,
} from 'lib/interfaces/ui-components';

const FOOTER_LAYOUT_OPTIONS = [
  { label: 'Vertical', value: ConfigValueDirection.Vertical },
  { label: 'Horizontal', value: ConfigValueDirection.Horizontal },
];

const StyledCheckboxContainer = styled.div`
  margin-top: ${Spacing[3]};
  label {
    font-size: ${({ theme }) => theme.text.paragraph.md.fontSize} !important;
  }
`;

type FooterLayoutStylesProps = {
  component: FooterComponentConfig;
  componentPreview: FooterComponent;
  editable: boolean;
  componentPath: string;
  submitStyle: (field: string, values: any) => void;
};

const FooterLayoutStyles = ({
  component,
  componentPath,
  editable,
  submitStyle,
  componentPreview,
}: FooterLayoutStylesProps) => {
  const dispatch = useDispatch();

  const onAlignmentToggleChange = useCallback(
    (value: string, field: string) => {
      submitStyle(field, value);
    },
    [submitStyle],
  );

  const onDirectionToggleChange = useCallback(
    (value: ConfigValueDirection, field: string) => {
      batch(() => {
        dispatch(
          change(
            INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
            `steps.${componentPath}.config.${field}`,
            value,
          ),
        );

        if (isHorizontalConfigSetting(value)) {
          onAlignmentToggleChange(StyleValueJustify.Center, 'justify');
        }
      });
    },
    [componentPath, dispatch, onAlignmentToggleChange],
  );

  const getDefaultedValue = (direction: ConfigValueDirection) => {
    return isHorizontalConfigSetting(direction)
      ? ConfigValueDirection.Horizontal
      : ConfigValueDirection.Vertical;
  };

  return (
    <SectionAccordion title='Layout'>
      <SectionRow>
        <SectionColumn>
          <ToggleStyleInput
            field='direction'
            editable={editable}
            name={`layout-${component.name}-direction-toggle`}
            defaultValue={getDefaultedValue(component.config.direction)}
            options={FOOTER_LAYOUT_OPTIONS}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onChange={onDirectionToggleChange}
          />
          <StyledCheckboxContainer>
            <Checkbox
              checked={component.config.direction === ConfigValueDirection.HorizontalReverse}
              disabled={component.config.direction === ConfigValueDirection.Vertical || !editable}
              onChange={() => {
                const isTrueHorizontal =
                  component.config.direction === ConfigValueDirection.Horizontal;
                onDirectionToggleChange(
                  isTrueHorizontal
                    ? ConfigValueDirection.HorizontalReverse
                    : ConfigValueDirection.Horizontal,
                  'direction',
                );
              }}
              label='Reverse direction'
              size={1}
            />
          </StyledCheckboxContainer>
        </SectionColumn>
      </SectionRow>
      {getDefaultedValue(component.config.direction) === ConfigValueDirection.Vertical && (
        <SectionRow>
          <SectionColumn>
            <ToggleStyleInput
              label='Alignment'
              field='justify'
              editable={editable}
              name={`layout-${component.name}-alignment-toggle`}
              defaultValue={componentPreview.styles?.justify?.base?.base ?? 'center'}
              options={[
                { label: <AlignLeftIcon size={12} />, value: StyleValueJustify.Start },
                {
                  label: <CenterAlignHorizontalIcon size={12} />,
                  value: StyleValueJustify.Center,
                },
                { label: <AlignRightIcon size={12} />, value: StyleValueJustify.End },
              ]}
              onChange={onAlignmentToggleChange}
            />
          </SectionColumn>
        </SectionRow>
      )}
    </SectionAccordion>
  );
};

export default FooterLayoutStyles;
